import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF, useAnimations } from '@react-three/drei';
import * as THREE from 'three';

const MODEL_URL = '/models/astra.glb';

// Preload so the first scroll feels instant
useGLTF.preload(MODEL_URL);

export default function AstraModel({ mouse, ...props }) {
  const group = useRef();
  const { scene, animations } = useGLTF(MODEL_URL);
  const { actions, names } = useAnimations(animations, group);

  // Auto-scale & center so android is ~1.8m tall with feet at y=0
  // Side-effecting normalisation runs once when the scene is first loaded.
  useEffect(() => {
    if (!scene) return;
    const box = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    const targetHeight = 1.85;
    const s = size.y > 0 ? targetHeight / size.y : 1;
    scene.position.set(-center.x * s, -box.min.y * s, -center.z * s);
    scene.scale.setScalar(s);
  }, [scene]);

  // Find head / eye bones once
  const bones = useMemo(() => {
    const result = { head: null, lEye: null, rEye: null };
    scene.traverse((obj) => {
      if (!obj.isBone && !obj.isObject3D) return;
      const n = (obj.name || '').toLowerCase();
      if (!result.head && n.includes('head') && !n.includes('headphone')) result.head = obj;
      if (!result.lEye && (n.includes('l_eye') || n.includes('lefteye'))) result.lEye = obj;
      if (!result.rEye && (n.includes('r_eye') || n.includes('righteye'))) result.rEye = obj;
    });
    return result;
  }, [scene]);

  // Enhance materials for a sleek holo look
  useEffect(() => {
    scene.traverse((obj) => {
      if (obj.isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if (!m) return;
          if ('metalness' in m) m.metalness = Math.min(1, (m.metalness ?? 0.5) + 0.2);
          if ('roughness' in m) m.roughness = Math.max(0.15, (m.roughness ?? 0.6) - 0.1);
          if ('envMapIntensity' in m) m.envMapIntensity = 1.2;
          m.needsUpdate = true;
        });
      }
    });
  }, [scene]);

  // Play idle animation (prefer Idle02_F -> idle -> first)
  useEffect(() => {
    if (!names || names.length === 0) return;
    const preferred =
      names.find((n) => /idle.*02.*f/i.test(n)) ||
      names.find((n) => /idle/i.test(n)) ||
      names[0];
    const action = actions[preferred];
    if (action) {
      action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.6).play();
      action.timeScale = 0.85;
    }
    return () => {
      Object.values(actions).forEach((a) => a && a.fadeOut(0.3));
    };
  }, [actions, names]);

  // Mouse tracking for head + eyes
  const targetHead = useRef(new THREE.Vector3());
  const targetEye = useRef(new THREE.Vector3());

  useFrame((state, delta) => {
    if (!mouse) return;
    // Convert normalized mouse (-1..1) to rotation targets
    const mx = mouse.x; // horizontal
    const my = mouse.y; // vertical (y up)

    // Head: subtle yaw + pitch
    if (bones.head) {
      const yaw = THREE.MathUtils.clamp(mx * 0.45, -0.5, 0.5);
      const pitch = THREE.MathUtils.clamp(-my * 0.28, -0.35, 0.35);
      // Apply relative to initial rotation (approximate: lerp to target)
      bones.head.rotation.y = THREE.MathUtils.damp(bones.head.rotation.y, yaw, 4, delta);
      bones.head.rotation.x = THREE.MathUtils.damp(bones.head.rotation.x, pitch, 4, delta);
    }

    // Eyes: sharper tracking
    [bones.lEye, bones.rEye].forEach((eye) => {
      if (!eye) return;
      const ey = THREE.MathUtils.clamp(mx * 0.35, -0.45, 0.45);
      const ex = THREE.MathUtils.clamp(-my * 0.2, -0.3, 0.3);
      eye.rotation.y = THREE.MathUtils.damp(eye.rotation.y, ey, 8, delta);
      eye.rotation.x = THREE.MathUtils.damp(eye.rotation.x, ex, 8, delta);
    });

    // Gentle breathing sway on the root group
    if (group.current) {
      const t = state.clock.elapsedTime;
      group.current.rotation.y = Math.sin(t * 0.25) * 0.04;
    }
  });

  return (
    <group ref={group} {...props} dispose={null}>
      <primitive object={scene} />
    </group>
  );
}
